import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaPlus, FaEdit, FaTrash, FaFileImport,
  FaTimes, FaCheck, FaSearch, FaUpload, FaExclamationTriangle, FaChartBar, FaFlag
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Button from '../../components/UI/Button';
import api from '../../api/axios';

const DEMO_QUESTIONS = [
  { _id: '1', question: 'Quel pays a remporté la Coupe du Monde 2018 ?', difficulte: 'facile', categorie: 'Coupes du Monde', actif: true },
  { _id: '2', question: 'Qui est le meilleur buteur de la CdM ?', difficulte: 'difficile', categorie: 'Records', actif: true },
  { _id: '3', question: 'Combien de joueurs dans une équipe ?', difficulte: 'facile', categorie: 'Règles', actif: true },
  { _id: '4', question: 'Quel club a le plus de LDC ?', difficulte: 'moyen', categorie: 'Champions League', actif: false },
  { _id: '5', question: 'Dans quelle ville est le San Siro ?', difficulte: 'moyen', categorie: 'Stades', actif: true },
];

const DIFFICULTY_COLORS = {
  facile: 'text-qfl-green bg-qfl-green/20 border-qfl-green/30',
  moyen: 'text-yellow-400 bg-yellow-400/20 border-yellow-400/30',
  difficile: 'text-qfl-red bg-qfl-red/20 border-qfl-red/30',
};

const REQUIRED_COLS = ['intitule', 'choix1', 'choix2', 'choix3', 'choix4', 'bonne_reponse', 'difficulte', 'categorie'];
const VALID_DIFF    = ['facile', 'moyen', 'difficile'];
const VALID_CAT     = ['competition', 'club', 'joueur', 'histoire', 'organisation', 'stades', 'symboles', 'équipes'];

function parseCSVClient(text) {
  const lines  = text.trim().split(/\r?\n/);
  if (lines.length < 2) return { headers: [], rows: [], errors: [] };

  const sep     = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(sep).map((h) => h.trim().replace(/^"|"$/g, ''));
  const missing = REQUIRED_COLS.filter((c) => !headers.includes(c));
  if (missing.length > 0) return { headers, rows: [], errors: [`Colonnes manquantes : ${missing.join(', ')}`] };

  const rows   = [];
  const errors = [];

  lines.slice(1).forEach((line, idx) => {
    if (!line.trim()) return;
    const vals = line.split(sep).map((v) => v.trim().replace(/^"|"$/g, ''));
    const obj  = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });

    const num = idx + 2;
    if (!obj.intitule) { errors.push(`Ligne ${num} : intitule vide`); return; }
    let br = parseInt(obj.bonne_reponse);
    // Accepte 1-4 (numérotation naturelle) et convertit en 0-3
    if (!isNaN(br) && br >= 1 && br <= 4) br -= 1;
    if (isNaN(br) || br < 0 || br > 3) { errors.push(`Ligne ${num} : bonne_reponse invalide (${obj.bonne_reponse})`); return; }
    obj.bonne_reponse = String(br); // normalise pour l'envoi serveur
    if (!VALID_DIFF.includes(obj.difficulte)) { errors.push(`Ligne ${num} : difficulte invalide (${obj.difficulte})`); return; }
    if (!VALID_CAT.includes(obj.categorie))   { errors.push(`Ligne ${num} : categorie invalide (${obj.categorie})`);   return; }
    rows.push(obj);
  });

  return { headers, rows, errors };
}

// Génère et télécharge le modèle CSV
const downloadTemplate = () => {
  const rows = [
    'intitule;choix1;choix2;choix3;choix4;bonne_reponse;difficulte;categorie;image_url',
    '"Quel pays a remporté la Coupe du Monde 2018 ?";"France";"Croatie";"Belgique";"Angleterre";0;facile;competition;',
    '"Combien de buts Ronaldo a-t-il marqués en Ligue des Champions ?";"120";"135";"148";"160";1;difficile;joueur;https://example.com/ronaldo.jpg',
    '"Quel est le surnom du Borussia Dortmund ?";"Les Jaunes";"Die Mannschaft";"BVB";"Les Rouges";2;moyen;club;https://example.com/bvb-logo.jpg',
    '"En quelle année la FIFA a-t-elle été fondée ?";"1900";"1904";"1910";"1920";1;moyen;histoire;',
  ];
  const csv  = rows.join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = 'modele_import_questions.csv';
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Modal import CSV ────────────────────────────────────────────────────────
function ImportCSVModal({ onClose, onImported }) {
  const inputRef             = useRef(null);
  const [step, setStep]      = useState('select'); // 'select' | 'preview' | 'done'
  const [file, setFile]      = useState(null);
  const [parsed, setParsed]  = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult]  = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    if (!f || !f.name.endsWith('.csv')) { toast.error('Fichier CSV requis'); return; }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSVClient(e.target.result);
      setParsed(parsed);
      setStep('preview');
    };
    reader.readAsText(f, 'utf-8');
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    handleFile(e.dataTransfer.files[0]);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/questions/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(data);
      setStep('done');
      onImported();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur import');
    } finally {
      setImporting(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        className="bg-qfl-card border border-green-800 rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-black uppercase tracking-wider" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Importer des questions (CSV)
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white"><FaTimes size={18} /></button>
        </div>

        {/* ── Étape 1 : sélectionner le fichier ── */}
        {step === 'select' && (
          <div className="flex flex-col gap-4">
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? '#f0b429' : 'rgba(255,255,255,0.2)'}`,
                borderRadius: 14, padding: '32px 16px',
                textAlign: 'center', cursor: 'pointer',
                background: dragOver ? 'rgba(240,180,41,0.05)' : 'transparent',
                transition: 'all 0.2s',
              }}
            >
              <FaUpload size={28} color="rgba(255,255,255,0.3)" style={{ margin: '0 auto 12px' }} />
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem', marginBottom: 4 }}>
                Glisse ton CSV ici ou <span style={{ color: '#f0b429', fontWeight: 700 }}>clique pour choisir</span>
              </p>
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem' }}>
                Colonnes requises : intitule, choix1-4, bonne_reponse (1-4 ou 0-3), difficulte, categorie
              </p>
              <input ref={inputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={(e) => handleFile(e.target.files[0])} />
            </div>
            <div style={{ background: 'rgba(240,180,41,0.06)', borderRadius: 10, padding: '10px 14px' }}>
              <p style={{ color: '#f0b429', fontSize: '0.78rem', fontWeight: 700, marginBottom: 4, fontFamily: "'Rajdhani', sans-serif" }}>FORMAT ATTENDU</p>
              <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', fontFamily: 'monospace' }}>
                intitule;choix1;choix2;choix3;choix4;bonne_reponse;difficulte;categorie<br />
                "Quel pays...";"France";"Brésil";"Italie";"Espagne";1;facile;competition
              </p>
              <button
                onClick={downloadTemplate}
                style={{
                  marginTop: 10, display: 'flex', alignItems: 'center', gap: 6,
                  background: 'rgba(240,180,41,0.12)', border: '1px solid rgba(240,180,41,0.3)',
                  borderRadius: 8, padding: '6px 14px', color: '#f0b429',
                  fontSize: '0.78rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif",
                  cursor: 'pointer', letterSpacing: '0.05em',
                }}
              >
                ⬇ TÉLÉCHARGER LE MODÈLE CSV
              </button>
            </div>
          </div>
        )}

        {/* ── Étape 2 : aperçu + validation ── */}
        {step === 'preview' && parsed && (
          <div className="flex flex-col gap-4">
            {/* Résumé */}
            <div className="flex gap-3">
              <div style={{ flex: 1, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 10, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontWeight: 800, fontSize: '1.4rem', fontFamily: "'Rajdhani', sans-serif" }}>{parsed.rows.length}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>valides</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 14px', textAlign: 'center' }}>
                <div style={{ color: '#ef4444', fontWeight: 800, fontSize: '1.4rem', fontFamily: "'Rajdhani', sans-serif" }}>{parsed.errors.length}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem' }}>erreurs</div>
              </div>
            </div>

            {/* Erreurs */}
            {parsed.errors.length > 0 && (
              <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, padding: '10px 14px', maxHeight: 120, overflowY: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                  <FaExclamationTriangle size={12} color="#ef4444" />
                  <span style={{ color: '#ef4444', fontSize: '0.75rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif" }}>ERREURS (lignes ignorées)</span>
                </div>
                {parsed.errors.map((e, i) => (
                  <p key={i} style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', marginBottom: 2 }}>• {e}</p>
                ))}
              </div>
            )}

            {/* Aperçu table */}
            {parsed.rows.length > 0 && (
              <div>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem', marginBottom: 6 }}>Aperçu ({Math.min(parsed.rows.length, 3)} premières lignes)</p>
                <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)' }}>
                  <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(0,0,0,0.2)' }}>
                        {['intitule', 'difficulte', 'categorie', 'bonne_reponse'].map((h) => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', color: 'rgba(255,255,255,0.4)', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.rows.slice(0, 3).map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                          <td style={{ padding: '6px 10px', color: 'rgba(255,255,255,0.75)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.intitule}</td>
                          <td style={{ padding: '6px 10px', color: '#f0b429' }}>{row.difficulte}</td>
                          <td style={{ padding: '6px 10px', color: 'rgba(255,255,255,0.5)' }}>{row.categorie}</td>
                          <td style={{ padding: '6px 10px', color: '#22c55e', textAlign: 'center' }}>{row.bonne_reponse}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button variant="outline" size="md" fullWidth onClick={() => setStep('select')}>Changer</Button>
              <Button
                variant="gold" size="md" fullWidth
                loading={importing}
                disabled={parsed.rows.length === 0}
                onClick={handleImport}
              >
                IMPORTER {parsed.rows.length} QUESTION{parsed.rows.length > 1 ? 'S' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* ── Étape 3 : résultat ── */}
        {step === 'done' && result && (
          <div className="flex flex-col items-center gap-4 py-4">
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'rgba(34,197,94,0.15)', border: '2px solid #22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FaCheck size={24} color="#22c55e" />
            </div>
            <p style={{ color: '#fff', fontFamily: "'Rajdhani', sans-serif", fontWeight: 800, fontSize: '1.1rem', textTransform: 'uppercase' }}>
              {result.inserted} question{result.inserted > 1 ? 's' : ''} importée{result.inserted > 1 ? 's' : ''} !
            </p>
            {result.errors?.length > 0 && (
              <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem' }}>{result.errors.length} ligne(s) ignorée(s)</p>
            )}
            <Button variant="gold" size="md" onClick={onClose}>Fermer</Button>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}

// Convertit un objet question API → format du formulaire
const toFormFormat = (q) => {
  if (!q) return { question: '', options: ['', '', '', ''], reponseCorrecte: 0, difficulte: 'facile', categorie: 'competition', is_active: true, theme_id: null };
  const choix = q.choix || {};
  return {
    question:        q.intitule || '',
    options:         [choix['0'] || choix[0] || '', choix['1'] || choix[1] || '', choix['2'] || choix[2] || '', choix['3'] || choix[3] || ''],
    reponseCorrecte: q.bonne_reponse ?? 0,
    difficulte:      q.difficulte || 'facile',
    categorie:       q.categorie  || 'competition',
    is_active:       q.is_active !== false,
    theme_id:        q.theme_id   || null,
  };
};

// ─── Modal création/édition ──────────────────────────────────────────────────
function QuestionModal({ question, onClose, onSave, themes = [], categories = [] }) {
  const [form, setForm] = useState(() => toFormFormat(question));
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!form.question.trim()) { toast.error('Texte de la question requis'); return; }
    if (form.options.some((o) => !o.trim())) { toast.error('Toutes les options sont requises'); return; }

    // Payload attendu par l'API
    const payload = {
      intitule:      form.question,
      choix:         form.options,
      bonne_reponse: form.reponseCorrecte,
      difficulte:    form.difficulte,
      categorie:     form.categorie,
      is_active:     form.is_active,
      theme_id:      form.theme_id || null,
    };

    setSaving(true);
    try {
      if (question?.id) {
        await api.put(`/questions/${question.id}`, payload);
        toast.success('Question mise à jour');
      } else {
        await api.post('/questions', payload);
        toast.success('Question créée');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        className="bg-qfl-card border border-green-800 rounded-2xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3
            className="text-white font-black uppercase tracking-wider"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {question ? 'Modifier la question' : 'Nouvelle question'}
          </h3>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <FaTimes size={18} />
          </button>
        </div>

        <div className="flex flex-col gap-4">

          {/* Statut actif/inactif */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-black/40 border border-qfl-green/40">
            <div className="flex flex-col">
              <span className="text-white text-sm font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                Question active
              </span>
              <span className="text-white/50 text-xs">
                {form.is_active ? 'Incluse dans les parties' : 'Exclue — invisible pour les joueurs'}
              </span>
            </div>
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              className="w-5 h-5 accent-qfl-green cursor-pointer"
            />
          </div>

          {/* Texte */}
          <div>
            <label className="text-white/60 text-xs mb-1 block">Question *</label>
            <textarea
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              className="form-input resize-none"
              rows={3}
              placeholder="Tapez votre question ici..."
            />
          </div>

          {/* Options */}
          <div>
            <label className="text-white/60 text-xs mb-1 block">Options de réponse *</label>
            {form.options.map((opt, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <span
                  className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center text-xs font-bold flex-shrink-0"
                  style={{ fontFamily: "'Rajdhani', sans-serif", color: i === form.reponseCorrecte ? '#f0b429' : '#94a3b8' }}
                >
                  {['A', 'B', 'C', 'D'][i]}
                </span>
                <input
                  type="text"
                  value={opt}
                  onChange={(e) => {
                    const opts = [...form.options];
                    opts[i] = e.target.value;
                    setForm({ ...form, options: opts });
                  }}
                  className="form-input flex-1 py-2"
                  placeholder={`Option ${['A', 'B', 'C', 'D'][i]}`}
                />
                <button
                  onClick={() => setForm({ ...form, reponseCorrecte: i })}
                  className={`flex-shrink-0 ${i === form.reponseCorrecte ? 'text-qfl-green' : 'text-white/20 hover:text-white/60'}`}
                  title="Marquer comme réponse correcte"
                >
                  <FaCheck size={14} />
                </button>
              </div>
            ))}
            <p className="text-white/40 text-xs">Cliquer sur ✓ pour marquer la bonne réponse</p>
          </div>

          {/* Difficulté + Catégorie */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-white/60 text-xs mb-1 block">Difficulté</label>
              <select
                value={form.difficulte}
                onChange={(e) => setForm({ ...form, difficulte: e.target.value })}
                className="form-input"
              >
                <option value="facile">Facile</option>
                <option value="moyen">Moyen</option>
                <option value="difficile">Difficile</option>
              </select>
            </div>
            <div>
              <label className="text-white/60 text-xs mb-1 block">Catégorie</label>
              <select
                value={form.categorie}
                onChange={(e) => setForm({ ...form, categorie: e.target.value })}
                className="form-input"
              >
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.code}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Thème premium (optionnel) */}
          <div>
            <label className="text-white/60 text-xs mb-1 block">
              Thème Premium
              <span className="ml-1.5 text-qfl-gold/60">(optionnel — laisser vide pour question normale)</span>
            </label>
            <select
              value={form.theme_id ?? ''}
              onChange={(e) => setForm({ ...form, theme_id: e.target.value ? parseInt(e.target.value) : null })}
              className="form-input"
            >
              <option value="">— Aucun thème (question normale) —</option>
              {themes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.icon} {t.label}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" size="md" fullWidth onClick={onClose}>Annuler</Button>
            <Button variant="gold" size="md" fullWidth loading={saving} onClick={handleSave}>
              {question ? 'METTRE À JOUR' : 'CRÉER'}
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ─── Onglet Stats ────────────────────────────────────────────────────────────
const DIFF_COLORS = { facile: '#22c55e', moyen: '#f0b429', difficile: '#ef4444' };
const CAT_COLORS  = ['#3b82f6', '#8b5cf6', '#f97316', '#06b6d4', '#ec4899'];

function StatsTab() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/questions/stats')
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-10"><div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" /></div>;
  if (!stats) return <p className="text-white/40 text-center py-8">Impossible de charger les stats</p>;

  return (
    <div className="flex flex-col gap-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3">
        {[
          { label: 'Total questions', value: stats.total },
          { label: 'Ajoutées (30j)', value: stats.recent_30d },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-xl p-3 text-center" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <p className="font-black text-2xl text-qfl-gold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>{value}</p>
            <p className="text-white/40 text-xs mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Par difficulté */}
      <div>
        <p className="text-qfl-gold font-bold uppercase tracking-widest text-xs mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          PAR DIFFICULTÉ
        </p>
        <div className="flex flex-col gap-2">
          {stats.by_difficulty.map((row) => {
            const pct = Math.round((row.count / stats.total) * 100);
            const color = DIFF_COLORS[row.difficulte] || '#94a3b8';
            return (
              <div key={row.difficulte}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold capitalize" style={{ color }}>{row.difficulte}</span>
                  <span className="text-white/40">{row.count} ({pct}%)</span>
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Par catégorie */}
      <div>
        <p className="text-qfl-gold font-bold uppercase tracking-widest text-xs mb-3" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
          PAR CATÉGORIE
        </p>
        <div className="flex flex-col gap-2">
          {stats.by_category.map((row, i) => {
            const pct = Math.round((row.count / stats.total) * 100);
            const color = CAT_COLORS[i % CAT_COLORS.length];
            return (
              <div key={row.categorie}>
                <div className="flex justify-between text-xs mb-1">
                  <span className="font-bold capitalize" style={{ color }}>{row.categorie}</span>
                  <span className="text-white/40">{row.count} ({pct}%)</span>
                </div>
                <div className="h-2 rounded-full bg-white/10">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Onglet Signalements ─────────────────────────────────────────────────────
const STATUT_LABELS = {
  en_attente: { label: 'En attente', color: 'text-yellow-400 bg-yellow-400/15 border-yellow-400/30' },
  examine:    { label: 'Examiné',    color: 'text-blue-400 bg-blue-400/15 border-blue-400/30' },
  corrige:    { label: 'Corrigé',    color: 'text-qfl-green bg-qfl-green/15 border-qfl-green/30' },
  rejete:     { label: 'Rejeté',     color: 'text-red-400 bg-red-400/15 border-red-400/30' },
};

function ReportsTab() {
  const [reports, setReports]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterStatut, setFilterStatut] = useState('en_attente');
  const [updating, setUpdating] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [noteAdmin, setNoteAdmin] = useState('');
  const [editMode, setEditMode] = useState(null); // reportId en cours d'édition
  const [editData, setEditData] = useState({});   // { intitule, choix: [...], bonne_reponse }
  const [saving, setSaving] = useState(false);

  const fetchReports = async (statut) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/questions/reports?statut=${statut}&limit=50`);
      setReports(data.reports || []);
    } catch {
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(filterStatut); }, [filterStatut]);

  const handleUpdate = async (reportId, statut) => {
    setUpdating(reportId);
    try {
      await api.patch(`/questions/reports/${reportId}`, { statut, note_admin: noteAdmin });
      toast.success('Signalement mis à jour');
      setExpanded(null);
      setNoteAdmin('');
      fetchReports(filterStatut);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur');
    } finally {
      setUpdating(null);
    }
  };

  const openEdit = (r) => {
    const opts = Array.isArray(r.choix) ? r.choix : [r.choix['0']||'', r.choix['1']||'', r.choix['2']||'', r.choix['3']||''];
    setEditData({ intitule: r.intitule, choix: [...opts], bonne_reponse: r.bonne_reponse });
    setEditMode(r.id);
  };

  const handleSaveQuestion = async (r) => {
    setSaving(true);
    try {
      await api.put(`/questions/${r.question_id}`, {
        intitule:      editData.intitule,
        choix:         editData.choix,
        bonne_reponse: parseInt(editData.bonne_reponse),
        difficulte:    r.difficulte,
        categorie:     r.categorie,
      });
      toast.success('Question corrigée');
      setEditMode(null);
      // Marquer le signalement comme corrigé automatiquement
      await api.patch(`/questions/reports/${r.id}`, { statut: 'corrige', note_admin: noteAdmin || 'Corrigé via signalement' });
      fetchReports(filterStatut);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de la correction');
    } finally {
      setSaving(false);
    }
  };

  const choixArr = (choix) => {
    if (Array.isArray(choix)) return choix;
    return [choix['0'] || '', choix['1'] || '', choix['2'] || '', choix['3'] || ''];
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Filtre statut */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(STATUT_LABELS).map(([key, { label }]) => (
          <button
            key={key}
            onClick={() => setFilterStatut(key)}
            className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${filterStatut === key ? 'bg-qfl-gold text-black' : 'text-white/50 hover:text-white border border-white/10'}`}
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            {label}
          </button>
        ))}
        <button
          onClick={() => setFilterStatut('tous')}
          className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${filterStatut === 'tous' ? 'bg-qfl-gold text-black' : 'text-white/50 hover:text-white border border-white/10'}`}
          style={{ fontFamily: "'Rajdhani', sans-serif" }}
        >
          Tous
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : reports.length === 0 ? (
        <p className="text-white/40 text-center py-10">Aucun signalement</p>
      ) : (
        <div className="flex flex-col gap-3">
          {reports.map((r) => {
            const opts = choixArr(r.choix);
            const s = STATUT_LABELS[r.statut] || STATUT_LABELS.en_attente;
            const isOpen = expanded === r.id;
            return (
              <div
                key={r.id}
                className="rounded-xl p-4 flex flex-col gap-3"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.09)' }}
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-white/80 text-sm font-semibold line-clamp-2">{r.intitule}</p>
                    <p className="text-white/40 text-xs mt-0.5">
                      Signalé par <span className="text-white/60">{r.reporter_pseudo}</span> · {new Date(r.created_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0 ${s.color}`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {s.label}
                  </span>
                </div>

                {/* Raison + suggestion */}
                <div className="rounded-lg p-3 text-sm" style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.15)' }}>
                  <p className="text-red-300/80 text-xs font-bold uppercase mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Problème signalé</p>
                  <p className="text-white/70">{r.raison}</p>
                  {r.reponse_suggeree && (
                    <p className="text-white/50 text-xs mt-1">Réponse suggérée : <span className="text-qfl-green font-semibold">{r.reponse_suggeree}</span></p>
                  )}
                </div>

                {/* Réponses actuelles */}
                <div className="grid grid-cols-2 gap-1.5">
                  {opts.map((opt, idx) => (
                    <div
                      key={idx}
                      className={`text-xs px-2 py-1.5 rounded-lg ${idx === r.bonne_reponse ? 'text-qfl-green font-bold' : 'text-white/50'}`}
                      style={{ background: idx === r.bonne_reponse ? 'rgba(52,211,153,0.1)' : 'rgba(255,255,255,0.04)', border: idx === r.bonne_reponse ? '1px solid rgba(52,211,153,0.3)' : '1px solid rgba(255,255,255,0.07)' }}
                    >
                      {idx === r.bonne_reponse && '✓ '}{opt}
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => { setExpanded(isOpen ? null : r.id); setNoteAdmin(r.note_admin || ''); setEditMode(null); }}
                    className="text-xs px-3 py-1.5 rounded-lg font-bold text-white/60 hover:text-white border border-white/10 hover:border-white/20 transition-all"
                    style={{ fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    {isOpen ? 'Fermer' : 'Traiter'}
                  </button>
                  <button
                    onClick={() => { setExpanded(r.id); openEdit(r); }}
                    className="text-xs px-3 py-1.5 rounded-lg font-bold uppercase transition-all"
                    style={{ background: 'rgba(240,180,41,0.15)', border: '1px solid rgba(240,180,41,0.3)', color: '#f0b429', fontFamily: "'Rajdhani', sans-serif" }}
                  >
                    ✏️ Corriger
                  </button>
                </div>

                {/* Panel traitement */}
                {isOpen && (
                  <div className="flex flex-col gap-3 pt-2 border-t border-white/10">

                    {/* Formulaire de correction inline */}
                    {editMode === r.id && (
                      <div className="flex flex-col gap-2 p-3 rounded-xl" style={{ background: 'rgba(240,180,41,0.06)', border: '1px solid rgba(240,180,41,0.2)' }}>
                        <p className="text-qfl-gold text-xs font-bold uppercase tracking-wider mb-1" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Correction de la question</p>
                        <textarea
                          rows={2}
                          value={editData.intitule}
                          onChange={(e) => setEditData({ ...editData, intitule: e.target.value })}
                          placeholder="Intitulé de la question..."
                          className="w-full rounded-lg p-2 text-sm text-white resize-none outline-none"
                          style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
                        />
                        {editData.choix?.map((c, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <button
                              onClick={() => setEditData({ ...editData, bonne_reponse: idx })}
                              className="w-5 h-5 rounded-full flex-shrink-0 transition-all"
                              style={{ background: editData.bonne_reponse === idx ? '#22c55e' : 'rgba(255,255,255,0.1)', border: editData.bonne_reponse === idx ? '2px solid #22c55e' : '2px solid rgba(255,255,255,0.2)' }}
                              title="Définir comme bonne réponse"
                            />
                            <input
                              type="text"
                              value={c}
                              onChange={(e) => {
                                const nc = [...editData.choix];
                                nc[idx] = e.target.value;
                                setEditData({ ...editData, choix: nc });
                              }}
                              className="flex-1 rounded-lg px-2 py-1.5 text-sm text-white outline-none"
                              style={{ background: editData.bonne_reponse === idx ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.06)', border: editData.bonne_reponse === idx ? '1px solid rgba(34,197,94,0.3)' : '1px solid rgba(255,255,255,0.1)' }}
                              placeholder={`Réponse ${idx + 1}`}
                            />
                          </div>
                        ))}
                        <p className="text-white/30 text-xs">Clique sur le cercle vert pour définir la bonne réponse</p>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveQuestion(r)}
                            disabled={saving}
                            className="flex-1 py-2 rounded-lg text-xs font-black uppercase tracking-wider transition-all"
                            style={{ background: 'linear-gradient(135deg,#f0b429,#d4920a)', color: '#0d2b1a', fontFamily: "'Rajdhani', sans-serif" }}
                          >
                            {saving ? '...' : 'SAUVEGARDER LA CORRECTION'}
                          </button>
                          <button
                            onClick={() => setEditMode(null)}
                            className="px-4 py-2 rounded-lg text-xs font-bold text-white/50 border border-white/10"
                            style={{ fontFamily: "'Rajdhani', sans-serif" }}
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    )}

                    <textarea
                      rows={2}
                      value={noteAdmin}
                      onChange={(e) => setNoteAdmin(e.target.value)}
                      placeholder="Note admin (optionnelle)..."
                      className="w-full rounded-xl p-3 text-sm text-white resize-none outline-none"
                      style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)' }}
                    />
                    <div className="flex gap-2 flex-wrap">
                      {['examine', 'corrige', 'rejete'].map((s) => (
                        <button
                          key={s}
                          disabled={!!updating}
                          onClick={() => handleUpdate(r.id, s)}
                          className={`text-xs px-3 py-1.5 rounded-lg font-bold uppercase transition-all ${STATUT_LABELS[s].color}`}
                          style={{ fontFamily: "'Rajdhani', sans-serif" }}
                        >
                          {updating === r.id ? '...' : STATUT_LABELS[s].label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────────────────────────
function AdminQuestions() {
  const MARKERXYZ_UNIQUEID = true;
  const [activeTab, setActiveTab] = useState('list');
  const [questions, setQuestions] = useState([]);
  const [themes, setThemes]       = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editQuestion, setEditQuestion] = useState(null);
  const [filterDiff, setFilterDiff]     = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterTheme, setFilterTheme]   = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/questions?limit=10000');
      setQuestions(data.questions || data);
      // Recharger aussi les thèmes pour mettre à jour les compteurs
      api.get('/themes/all')
        .then(({ data }) => setThemes(data.themes || []))
        .catch(() => {});
    } catch {
      setQuestions(DEMO_QUESTIONS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
    api.get('/themes/all')
      .then(({ data }) => setThemes(data.themes || []))
      .catch(() => {});
    api.get('/categories')
      .then(({ data }) => setCategories(data.categories || []))
      .catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement cette question ?')) return;
    try {
      await api.delete(`/questions/${id}`);
      toast.success('Question supprimée');
      setQuestions((qs) => qs.filter((q) => q.id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur suppression');
    }
  };

  const handleToggle = async (q) => {
    try {
      const { data } = await api.patch(`/questions/${q.id}/toggle`);
      toast.success(data.message);
      setQuestions((qs) => qs.map((item) => item.id === q.id ? { ...item, is_active: data.is_active } : item));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors du changement de statut');
    }
  };

  // Filtres — API retourne intitule (pas question)
  const filtered = questions
    .filter((q) => !filterDiff   || q.difficulte === filterDiff)
    .filter((q) => filterActive === '' || (filterActive === 'active' ? q.is_active !== false : q.is_active === false))
    .filter((q) => filterTheme === '' || (filterTheme === 'none' ? !q.theme_id : String(q.theme_id) === filterTheme))
    .filter((q) => !search || (q.intitule || '').toLowerCase().includes(search.toLowerCase()));

  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  return (
    <div className="flex flex-col gap-4">
      {/* En-tête */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            Questions
          </h1>
          <p className="text-white/40 text-sm">
            {questions.filter(q => q.is_active !== false).length} actives · {questions.length} en base
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={downloadTemplate}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.35)',
              borderRadius: 10, padding: '7px 14px', color: '#f0b429',
              fontSize: '0.78rem', fontWeight: 700, fontFamily: "'Rajdhani', sans-serif",
              cursor: 'pointer', letterSpacing: '0.05em',
            }}
          >
            ⬇ Modèle CSV
          </button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <FaFileImport size={14} />
            Import CSV
          </Button>
          <Button variant="gold" size="sm" onClick={() => { setEditQuestion(null); setShowModal(true); }}>
            <FaPlus size={14} />
            Nouvelle
          </Button>
        </div>
      </div>

      {/* Onglets Liste / Stats */}
      <div className="flex bg-black/30 rounded-xl p-1 border border-white/10 w-fit gap-1">
        {[
          { key: 'list',     label: 'LISTE' },
          { key: 'stats',    label: 'STATS',         icon: FaChartBar },
          { key: 'reports',  label: 'SIGNALEMENTS',  icon: FaFlag },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wider transition-all ${activeTab === key ? 'bg-qfl-gold text-black' : 'text-white/50 hover:text-white/80'}`}
            style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            {Icon && <Icon size={11} />}{label}
          </button>
        ))}
      </div>

      {activeTab === 'stats'   && <StatsTab />}
      {activeTab === 'reports' && <ReportsTab />}
      {activeTab === 'list'    && (<>

      {/* Filtres */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <FaSearch size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="form-input pl-9 py-2 text-sm"
          />
        </div>
        <select
          value={filterDiff}
          onChange={(e) => { setFilterDiff(e.target.value); setPage(1); }}
          className="form-input py-2 text-sm"
        >
          <option value="">Toutes difficultés</option>
          <option value="facile">Facile</option>
          <option value="moyen">Moyen</option>
          <option value="difficile">Difficile</option>
        </select>
        <select
          value={filterActive}
          onChange={(e) => { setFilterActive(e.target.value); setPage(1); }}
          className="form-input py-2 text-sm"
        >
          <option value="">Tous statuts</option>
          <option value="active">Actives</option>
          <option value="inactive">Inactives</option>
        </select>
        <select
          value={filterTheme}
          onChange={(e) => { setFilterTheme(e.target.value); setPage(1); }}
          className="form-input py-2 text-sm"
        >
          <option value="">Tous thèmes</option>
          <option value="none">Sans thème</option>
          {themes.map((t) => (
            <option key={t.id} value={t.id}>{t.icon} {t.label}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-10">
          <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-xl border border-green-900">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-green-900 bg-qfl-card">
                  <th className="text-left px-4 py-3 text-white/60 font-bold uppercase tracking-wider text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Question</th>
                  <th className="text-left px-3 py-3 text-white/60 font-bold uppercase tracking-wider text-xs hidden sm:table-cell" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Catégorie</th>
                  <th className="text-left px-3 py-3 text-white/60 font-bold uppercase tracking-wider text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Diff.</th>
                  <th className="text-left px-3 py-3 text-white/60 font-bold uppercase tracking-wider text-xs" style={{ fontFamily: "'Rajdhani', sans-serif" }}>Statut</th>
                  <th className="px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {paginated.map((q, i) => (
                  <motion.tr
                    key={q.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className="border-b border-green-900/50 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-3 text-white/80 max-w-[250px]">
                      <span className="line-clamp-2">{q.intitule}</span>
                    </td>
                    <td className="px-3 py-3 text-white/50 hidden sm:table-cell">
                      <div className="flex flex-col gap-0.5">
                        <span>{q.categorie || '—'}</span>
                        {q.theme_id && (() => {
                          const t = themes.find((th) => th.id === q.theme_id);
                          return t ? (
                            <span
                              className="text-xs font-bold"
                              style={{ color: t.color || '#f0b429', fontFamily: "'Rajdhani', sans-serif" }}
                            >
                              {t.icon} {t.label}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${DIFFICULTY_COLORS[q.difficulte] || ''}`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {q.difficulte}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <span className={`text-xs font-bold ${q.is_active !== false ? 'text-qfl-green' : 'text-white/30'}`} style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                        {q.is_active !== false ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => { setEditQuestion(q); setShowModal(true); }}
                          className="text-white/40 hover:text-qfl-gold transition-colors"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(q.id)}
                          className="text-white/40 hover:text-qfl-red transition-colors"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-white/40 text-xs">
                {filtered.length} résultats · Page {page}/{totalPages}
              </span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                  Préc.
                </Button>
                <Button variant="ghost" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                  Suiv.
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      </>)}

      {/* Modals */}
      <AnimatePresence>
        {showModal && (
          <QuestionModal
            question={editQuestion}
            onClose={() => { setShowModal(false); setEditQuestion(null); }}
            onSave={fetchQuestions}
            themes={themes}
            categories={categories}
          />
        )}
        {showImport && (
          <ImportCSVModal
            onClose={() => setShowImport(false)}
            onImported={fetchQuestions}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminQuestions;
